import React from "react"
import { AcceptLineLinkCommand } from "src/openapi"
import { AcceptInvitationCommand } from "../../openapi/models/AcceptInvitationCommand"
import { InvitationService } from "../../openapi/services/InvitationService"

export const useInvitation = (
  jwtCode: string
): {
  companyName: string
  tagNames: string[]
  listMigrationUrl: string | null | undefined
  signup: (command: AcceptInvitationCommand) => Promise<void>
} => {
  if (!jwtCode) {
    throw new Error("Invalid invitation code")
  }
  const base64Url = jwtCode.split(".")[1]
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/")
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
      .join("")
  )
  const payload = JSON.parse(jsonPayload)
  const companyId = Number(payload.aud[0])
  const companyName = payload["https://id.triple-three.io/companyName"] as string
  const tagNames = payload["https://id.triple-three.io/tagNames"] as string[]
  const embeddedListMigrationUrl =
    (payload["https://id.triple-three.io/listMigrationUrl"] as string | undefined) || null
  const [listMigrationUrl, setListMigrationUrl] = React.useState<string | null | undefined>(undefined)

  React.useEffect(() => {
    let active = true

    const decodeInvitation = async () => {
      try {
        const response = await InvitationService.decodeCompanyInvitationCode(companyId, {
          invitationCode: jwtCode,
        })
        if (!active) {
          return
        }
        setListMigrationUrl(response.listMigrationUrl || embeddedListMigrationUrl || null)
      } catch {
        if (!active) {
          return
        }
        setListMigrationUrl(embeddedListMigrationUrl || null)
      }
    }

    void decodeInvitation()

    return () => {
      active = false
    }
  }, [companyId, jwtCode])

  const signup = async (command: AcceptInvitationCommand) => {
    await InvitationService.acceptInvitation(companyId, command)
  }

  return {
    companyName,
    tagNames,
    listMigrationUrl,
    signup,
  }
}

export const useLinkLineAccount = (
  jwtCode: string
): {
  companyName: string
  userName: string
  linkLineAccount: (command: AcceptLineLinkCommand) => Promise<void>
} => {
  const base64Url = jwtCode.split(".")[1]
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/")
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
      .join("")
  )
  const payload = JSON.parse(jsonPayload)
  const userName = payload["https://id.triple-three.io/userName"] as string
  const companyName = payload["https://id.triple-three.io/companyName"] as string

  const linkLineAccount = async (command: AcceptLineLinkCommand) => {
    await InvitationService.linkLineAccount(command)
  }

  return {
    companyName,
    userName,
    linkLineAccount,
  }
}
